/**
 * The Random class provides utility methods for generating various types of random values,
 * including integers, floating-point numbers, bits, and colors.
 */
export class Random {
  /**
   * Generates a random integer between min (inclusive) and max (inclusive).
   * @param {number} min - The minimum value (inclusive).
   * @param {number} max - The maximum value (inclusive).
   * @returns {number} A random integer between min and max.
   */
  static integer(min, max) {
    if (min > max) {
      throw new Error("Min value must be less than or equal to max value.");
    }
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Generates a random floating-point number between min (inclusive) and max (exclusive).
   * @param {number} min - The minimum value (inclusive).
   * @param {number} max - The maximum value (exclusive).
   * @param {number} [precision=2] - The number of decimal places (optional).
   * @returns {number} A random floating-point number between min and max.
   */
  static float(min, max, precision = 2) {
    if (min > max) {
      throw new Error("Min value must be less than or equal to max value.");
    }
    const factor = 10 ** precision; // Determine the factor for precision
    return Math.round((Math.random() * (max - min) + min) * factor) / factor;
  }

  /**
   * Generates a random bit (0 or 1).
   * @returns {number} A random bit (0 or 1).
   */
  static bit() {
    return Math.random() < 0.5 ? 0 : 1;
  }

  /**
   * Generates a random color in hexadecimal format.
   * @returns {string} A random color in hexadecimal format (e.g., '#aabbcc').
   */
  static hexColor() {
    const randomColor = Math.floor(Math.random() * 0xffffff); // Generate a random number from 0 to 0xffffff
    return `#${randomColor.toString(16).padStart(6, "0")}`; // Convert to hexadecimal string and pad with leading zeros if necessary
  }

  /**
   *
   * @param {*} min
   * @param {*} max
   * @returns
   */
  static vector3(min = -1, max = 1, integer = true) {
    return {
      x: integer ? Random.integer(min, max) : Random.float(min, max),
      y: integer ? Random.integer(min, max) : Random.float(min, max),
      z: integer ? Random.integer(min, max) : Random.float(min, max),
    };
  }

  /**
   *
   * @param {*} min
   * @param {*} max
   * @param {*} integer
   * @returns
   */
  static vector2(min = -1, max = 1, integer = true) {
    return {
      x: integer ? Random.integer(min, max) : Random.float(min, max),
      y: integer ? Random.integer(min, max) : Random.float(min, max),
    };
  }
}
